<template>
    <pendo-popover-v2
        ref="popoverRef"
        :title="option.title"
        placement="right-start"
        distance="8"
        :hover="false"
        :container="false"
        keyboard-navigation
        :delay="{ show: 0, hide: 0 }"
        :disabled="!!option.route"
        popper-class="side-nav-option-popover"
        @show="onPopoverOpen"
        @hide="onPopoverClose">
        <template #default="{ shown }">
            <component
                :is="option.route ? 'pendo-router-link' : 'button'"
                v-pendo-tooltip="{
                    classes: 'side-nav-tooltip',
                    content: parentTooltipContent,
                    placement: 'right',
                    arrow: true,
                }"
                class="collapsible-nav-option nav-option"
                :data-cy="'collapsible-nav-option-' + option.name"
                tabindex="0"
                :pendo-analytics="option.pendoAnalytics"
                :class="{
                    'selected': isParentOptionActive,
                    expanded,
                    'is-open': shown,
                    'disabled': option.disabled,
                }"
                :disabled="option.disabled"
                :aria-label="option.title"
                :aria-current="isParentOptionActive && 'page'"
                :to="option.route ?? null"
                @mouseenter="handleMouseenter"
                @mouseleave="handleMouseleave">
                <div
                    class="pendo-collapse-item__header"
                    :class="{ expanded }">
                    <div class="nav-icon">
                        <component :is="option.iconComponent" />
                    </div>
                    <div
                        v-if="expanded"
                        class="pendo-collapse-item__title">
                        {{ option.title }}
                    </div>
                    <pendo-tag
                        v-if="option.tag && expanded"
                        :type="option.tag.type"
                        :size="option.tag.size || 'medium'">
                        <template
                            v-if="option.tag.label"
                            #default>
                            {{ option.tag.label }}
                        </template>
                    </pendo-tag>
                </div>
            </component>
        </template>
        <template #popover>
            <div
                ref="sideNavPopoverContent"
                class="side-nav-option-popover__content"
                @mouseenter="hasChildren && $emit('mouseenter', option.name)"
                @mouseleave="hasChildren && $emit('mouseleave')">
                <h2
                    :id="`${option.title}-popover`"
                    class="side-nav-option-popover__title">
                    {{ option.title }}
                </h2>
                <ul class="side-nav-option-popover__items">
                    <li
                        v-for="child in option.children"
                        :key="child.name">
                        <pendo-router-link
                            class="side-nav-option-popover__link"
                            :class="{
                                selected: isChildOptionActive(child),
                            }"
                            :disabled="child.disabled"
                            :aria-label="child.title"
                            :to="child.route"
                            :aria-current="isChildOptionActive(child) && 'page'"
                            :pendo-analytics="child.pendoAnalytics"
                            @click="handleChildClick"
                            @keydown.enter="handleChildClick"
                            @keydown.space="handleChildClick"
                            @keydown.tab.prevent="handleChildClick">
                            <div
                                v-pendo-tooltip="{
                                    content: tooltipContent,
                                    arrow: true,
                                    placement: 'right',
                                    delay: { show: 1000, hide: 0 },
                                }"
                                class="side-nav-option-popover__link-text"
                                @mouseover="onMouseover($event, child)">
                                {{ child.title }}
                            </div>
                            <pendo-tag
                                v-if="showTag(child) && child.tag.type !== 'UpsellTag'"
                                :type="child.tag.type"
                                :size="child.tag.size || 'medium'"
                                class="collapsible-nav-option__child-tag" />
                            <upsell-tag
                                v-if="showTag(child) && child.tag.type === 'UpsellTag'"
                                :size="child.tag.size || 'medium'"
                                class="side-nav-option-popover__link-tag" />
                        </pendo-router-link>
                    </li>
                </ul>
            </div>
        </template>
    </pendo-popover-v2>
</template>

<script>
import UpsellTag from '@/common/tags/upsell-tag.vue';
import AnalyticsNavIcon from '@/side-nav/components/nav-icons/analytics-nav-icon.vue';
import FeatureFlagsNavIcon from '@/side-nav/components/nav-icons/feature-flags-nav-icon.vue';
import GuidesNavIcon from '@/side-nav/components/nav-icons/guides-nav-icon.vue';
import ListenNavIcon from '@/side-nav/components/nav-icons/listen-nav-icon.vue';
import NpsNavIcon from '@/side-nav/components/nav-icons/nps-nav-icon.vue';
import OrchestrateNavIcon from '@/side-nav/components/nav-icons/orchestrate-nav-icon.vue';
import PeopleNavIcon from '@/side-nav/components/nav-icons/people-nav-icon.vue';
import PredictNavIcon from '@/side-nav/components/nav-icons/predict-nav-icon.vue';
import ProductNavIcon from '@/side-nav/components/nav-icons/product-nav-icon.vue';
import ReplayNavIcon from '@/side-nav/components/nav-icons/replay-nav-icon.vue';
import RoadmapNavIcon from '@/side-nav/components/nav-icons/roadmap-nav-icon.vue';
import SurveysNavIcon from '@/side-nav/components/nav-icons/surveys-nav-icon.vue';
import { isOptionActive } from '@/side-nav/helpers/side-nav.helper';
import { elementHasOverflowX } from '@/utils/dom.utils';
import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import { PendoPopoverV2, PendoTag, PendoTooltip } from '@pendo/components-vue3';
import { ref } from 'vue';
import { mapGetters } from 'vuex';

export default {
    components: {
        PendoPopoverV2,
        PendoTag,
        PendoRouterLink,
        UpsellTag,
        ProductNavIcon,
        PeopleNavIcon,
        AnalyticsNavIcon,
        FeatureFlagsNavIcon,
        ReplayNavIcon,
        ListenNavIcon,
        GuidesNavIcon,
        SurveysNavIcon,
        NpsNavIcon,
        OrchestrateNavIcon,
        PredictNavIcon,
        RoadmapNavIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        option: {
            type: Object,
            required: true,
        },
        activeOption: {
            type: Object,
            required: true,
        },
        expanded: {
            type: Boolean,
            default: false,
        },
        openPopover: {
            type: String,
            default: '',
        },
    },
    emits: ['handlePopover', 'mouseenter', 'mouseleave'],
    setup () {
        const sideNavPopoverContent = ref(null);

        return {
            sideNavPopoverContent,
        };
    },
    data () {
        return {
            tooltipContent: null,
        };
    },
    computed: {
        ...mapGetters({
            isInTrial: 'subscription/isInTrial',
        }),
        showPopover () {
            return this.openPopover === this.option.name;
        },
        collapsedNavTooltipContent () {
            return !this.option.collapsible && this.option.title;
        },
        parentTooltipContent () {
            if (this.option.tooltipMessage) {
                return this.option.tooltipMessage;
            }

            return this.collapsedNavTooltipContent;
        },
        isParentOptionActive () {
            return isOptionActive(this.option.name, this.activeOption.topOptionName);
        },
        hasChildren () {
            return this.option.children?.length > 0;
        },
    },
    watch: {
        showPopover: {
            async handler (showPopover) {
                await this.$nextTick();
                const ref = this.$refs.popoverRef;
                if (showPopover) {
                    if (!ref?.isShown) {
                        ref?.show?.();
                    }
                } else if (ref?.isShown) {
                    ref.hide?.();
                }
            },
        },
        immediate: true,
    },
    methods: {
        isChildOptionActive (child) {
            return isOptionActive(child.name, this.activeOption.secondaryOptionName);
        },
        onMouseover ({ currentTarget }, child) {
            if (child.tooltipMessage) {
                this.tooltipContent = child.tooltipMessage;

                return;
            }

            const { clientWidth, scrollWidth } = currentTarget;
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });
            if (hasOverflow) {
                this.tooltipContent = child.title;
            } else {
                this.tooltipContent = null;
            }
        },
        onPopoverOpen () {
            setTimeout(() => {
                if (this.openPopover && this.openPopover !== this.option.name) return;

                this.$emit('handlePopover', this.option.name);
                const firstOption = this.$refs.sideNavPopoverContent?.querySelector('.side-nav-option-popover__link');
                if (firstOption) {
                    firstOption.focus();
                }
            }, 50);
        },
        onPopoverClose () {
            if (this.openPopover === this.option.name || !this.openPopover) {
                this.$emit('handlePopover');
            }
        },
        handleMouseenter () {
            if (this.hasChildren) {
                this.$emit('mouseenter', this.option.name);
            }
        },
        handleMouseleave () {
            if (this.hasChildren) {
                this.$emit('mouseleave');
            }
        },
        showTag (child) {
            const redirect = child.hasRedirect?.();

            return (
                (child.tag?.type === 'UpsellTag' && !this.isInTrial && redirect?.name === 'UpgradeRequired') ||
                (!!child.tag && child.tag?.type !== 'UpsellTag')
            );
        },
        handleChildClick ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            if ($event.type === 'keydown') {
                $event.currentTarget.click();
            }

            this.$refs.popoverRef?.hide?.();
        },
    },
};
</script>

<style scoped lang="less">
@import '@/side-nav/nav.style.less';

a.collapsible-nav-option, button.collapsible-nav-option {
    display: block;

    &:not(.expanded):focus-visible {
        .nav-icon {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -2px;
        }
    }

    &.expanded {
        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
        }
    }
}

.collapsible-nav-option {
    display: block;
    padding: 0 !important;
    opacity: unset;
    height: unset;

    &:first-of-type:not(a) {
        :deep(.pendo-collapse-item__header) {
            margin-top: 0px;
        }
    }

    :deep(.pendo-collapse-item__header) {
        height: 32px;
        line-height: unset;
        border: none;
        border-radius: 5px;
        padding: 0;
        color: var(--text-body-default-base);
        font-size: 13.5px;
        display: flex;
        gap: 0;
        justify-content: flex-start;
        transition: background-color ease-in 150ms;
        font-weight: 500;
        background-color: unset;

        .pendo-collapse-item__title {
            margin-left: 6px;
        }

        .pendo-tag {
            margin-left: auto;
        }

        svg {
            height: 18px;
            width: 18px;
            padding: 4px;
            box-sizing: content-box;
            display: flex;
            align-items: flex-start;
            border-radius: 5px;
        }

        &:hover,
        &.popover-open {
            cursor: pointer;

            .filled-icon {
                opacity: 1;
            }

            .empty-icon {
                opacity: 0;
            }
        }
    }

    &.expanded {
        :deep(.pendo-collapse-item__header) {
            padding: 6px 8px;

            &:focus-visible {
                outline: 2px solid var(--palette-gray-90);
                outline-offset: -2px;
            }

            &:hover {
                background-color: var(--surface-secondary-hover);
            }
        }

        a {
            display: block;
        }
    }

    &:not(.expanded) :deep(.pendo-collapse-item__header) {
        justify-content: center;

        .nav-icon {
            padding: 2px;
            border-radius: 5px;
        }

        &:focus-visible {
            .nav-icon {
                outline: 2px solid var(--palette-gray-90);
                outline-offset: -2px;
            }
        }

        &:hover,
        &.popover-open {
            .nav-icon {
                background-color: var(--surface-secondary-hover);
            }
        }
    }

    &.disabled {
        cursor: not-allowed;
        opacity: 0.5;

        :deep(.pendo-collapse-item__header) {
            pointer-events: none;
        }
    }

    &:hover,
    &.popover-open,
    &.is-open,
    &.selected {
        :deep(.filled-icon) {
            opacity: 1;
        }

        :deep(.empty-icon) {
            opacity: 0;
        }
    }

    &.is-expanded {
        :deep(.filled-icon) {
            opacity: 1;
        }

        :deep(.empty-icon) {
            opacity: 0;
        }
    }
}

</style>

<style lang="less">
.pendo-popover-v3__reference:has(> .collapsible-nav-option) {
    width: 100%;
    height: 34px;
    display: flex;
    justify-content: center;

    a {
        text-decoration: none;
        color: var(--text-body-default-base);
    }
}
</style>
